import { useCallback, useEffect, useState } from "react";
import type { Oekt } from "../state/session";
import * as rapporter from "../state/rapporter";
import { useLevende } from "../state/levende";

/**
 * Leverte rapporter.
 *
 * ÉN BOKS PER REGI, som loggflata, og av samme grunn: rapporten er regis
 * dokument om dagen sin, og den som leter etter en leter etter hvem som
 * skrev den — ikke etter en dato i en felles liste.
 *
 * NYESTE ØVERST, og ikke filtrert på dagvelgeren. En rapport leses stort
 * sett i ettertid; «hva sto det på dag 6?» er hele grunnen til å ta vare på
 * dem. Boksen er et arkiv, ikke et dagsbilde.
 *
 * FLERE VERSJONER ER FLERE RAPPORTER. Retter regi noe og sender på nytt,
 * øker versjonen og begge blir stående — den som har lest v1 skal kunne se
 * at det kom en v2.
 *
 * OPPSUMMERINGEN STÅR FRAMME. Det er det eneste regi har skrevet med egne
 * ord om hele dagen, og i de fleste tilfeller er det den redaktøren er ute
 * etter. PDF-en er hele loggen, og den åpnes når man vil ha den.
 */

function dato(iso: string): string {
  if (iso === "") return "";
  const d = new Date(`${iso}T12:00:00`);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("no-NO", { day: "numeric", month: "short" });
}

function tidspunkt(iso: string | null): string {
  if (iso === null) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const idag = new Date().toDateString() === d.toDateString();
  const klokke = d.toLocaleTimeString("no-NO", { hour: "2-digit", minute: "2-digit" });
  return idag
    ? `i dag ${klokke}`
    : `${d.toLocaleDateString("no-NO", { day: "numeric", month: "short" })} ${klokke}`;
}

export function RapporterScreen({ oekt }: { readonly oekt: Oekt }): JSX.Element {
  const [kasser, setKasser] = useState<readonly rapporter.Rapportkasse[]>([]);
  const [laster, setLaster] = useState(true);
  /** Hvilken rapport som åpnes akkurat nå. Lenka hentes ved trykket. */
  const [aapner, setAapner] = useState<string | null>(null);
  /* Lenka lot seg ikke lage. Uten dette gjorde knappen ingenting, og den
     som trykket hadde ingen måte å vite hvorfor. */
  const [aapnefeil, setAapnefeil] = useState<string | null>(null);

  const last = useCallback((): void => {
    void (async () => {
      setKasser(await rapporter.hentRapporter(oekt));
      setLaster(false);
    })();
  }, [oekt]);

  useEffect(last, [last]);
  useLevende("rapporter", ["report"], last);

  const aapne = useCallback(async (r: rapporter.Rapport): Promise<void> => {
    if (r.filsti === null) return;
    setAapner(r.id);
    setAapnefeil(null);
    const url = await rapporter.lenke(r.filsti);
    setAapner(null);
    if (url === null) {
      setAapnefeil(r.id);
      return;
    }
    /*
     * Ny fane, ikke nedlasting. Redaktøren leser den og går videre — en fil
     * i nedlastingsmappa er noe man må rydde opp i etterpå.
     */
    window.open(url, "_blank", "noopener,noreferrer");
  }, []);

  if (laster) {
    return (
      <div className="flate"><div className="flate__innhold">
        <p className="synk__melding">Henter rapportene …</p>
      </div></div>
    );
  }

  if (kasser.length === 0) {
    return (
      <div className="flate"><div className="flate__innhold">
        <p className="synk__melding">
          Ingen rapporter levert ennå. Her kommer én boks per regi, med alle
          rapportene deres — nyeste øverst. Flata følger ikke dagvelgeren:
          en rapport leses som oftest i ettertid.
        </p>
      </div></div>
    );
  }

  return (
    <div className="flate">
      <div className="flate__innhold">
        <div className="logger">
          {kasser.map((k) => (
            <section className="loggkort" key={k.id} aria-label={`Rapporter fra ${k.forfatter}`}>
              <header className="loggkort__hode">
                <span className="loggkort__merke mono">{k.initialer}</span>
                <span className="loggkort__navn">{k.forfatter}</span>
                <span className="loggkort__alder mono">
                  {k.rapporter.length} {k.rapporter.length === 1 ? "rapport" : "rapporter"}
                </span>
              </header>

              <div className="rapporter">
                {k.rapporter.map((r) => (
                  <article className="rapport" key={r.id}>
                    <div className="rapport__hode">
                      <span className="rapport__dag mono">
                        Dag {r.opptaksdag}
                        {dato(r.dato) === "" ? "" : ` · ${dato(r.dato)}`}
                      </span>
                      <span className="rapport__versjon mono">v{r.versjon}</span>
                    </div>

                    <div className="rapport__meta mono">
                      {[r.lag, r.skift].filter((x) => x !== "").join(" · ")}
                      {r.levert === null ? "" : ` · levert ${tidspunkt(r.levert)}`}
                    </div>

                    {r.oppsummering === "" ? null : (
                      <p className="rapport__oppsummering">{r.oppsummering}</p>
                    )}

                    {r.filsti === null ? (
                      /* Raden finnes, fila gjorde det ikke. Det skal ikke
                         kunne skje — opplastingen tar fila først — men om
                         det gjør det, skal flata si det og ikke tilby en
                         knapp som ikke virker. */
                      <span className="rapport__mangler">PDF-en mangler</span>
                    ) : (
                      <>
                        <button
                          type="button"
                          className="rapport__aapne"
                          onClick={() => void aapne(r)}
                          disabled={aapner === r.id}
                        >
                          {aapner === r.id ? "Åpner …" : "Åpne PDF"}
                        </button>
                        {aapnefeil === r.id ? (
                          <span className="rapport__mangler">
                            Fikk ikke åpnet PDF-en. Prøv igjen.
                          </span>
                        ) : null}
                      </>
                    )}
                  </article>
                ))}
              </div>
            </section>
          ))}
        </div>
      </div>
    </div>
  );
}
